import Chart from 'chart.js/auto';
import { t, getLocale } from '/src/i18n.js';

class CoffeeStats extends HTMLElement {
 
    // ── A. Shadow DOM setup ──────────────────────────────────────────────
    constructor() {
      super();
      this._shadow = this.attachShadow({ mode: "open" });
      this._raw    = {};
      this._view   = "week";
      this._offset = 0;
      this._chart  = null;
      this._title = t("coffee-tracker");
    }
 
    // ── B. Lifecycle ─────────────────────────────────────────────────────
    connectedCallback() {
      this._render();
      this._bindEvents();
    }
 
    // ── C. JS property setter ────────────────────────────────────────────
    set data(obj) {
      this._raw    = (obj && typeof obj === "object") ? obj : {};
      this._offset = 0;
      this._update();
    }

    set title(title) {
      this._title = title;
      this._renderTitle(this._title);
    }
 
    // ── D. Attribute → property bridge ───────────────────────────────────
    static get observedAttributes() { return ["data"]; }
    attributeChangedCallback(name, _, newVal) {
      if (name === "data") {
        try { this.data = JSON.parse(newVal); } catch {}
      }
    }
 
    // ── E. Date helpers ──────────────────────────────────────────────────
 
    _sortedKeys() {
      return Object.keys(this._raw).sort();
    }
 
    _addDays(dateStr, n) {
      const d = new Date(dateStr + "T00:00:00");
      d.setDate(d.getDate() + n);
      // Use local date components to avoid timezone shift from toISOString()
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }
 
    // Returns the Monday of the week containing dateStr
    _weekStart(dateStr) {
      const d = new Date(dateStr + "T00:00:00");
      const day = d.getDay() || 7;
      d.setDate(d.getDate() - day + 1);
      // Use local date components to avoid timezone shift from toISOString()
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const dayNum = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${dayNum}`;
    }
 
    // ── F. Week view ──────────────────────────────────────────────────────
    // 7 days from Monday of (latest week + offset weeks)
    _weekData() {
      const keys        = this._sortedKeys();
      const today       = new Date();
      const latestDate  = today.toISOString().slice(0, 10);
      const anchorStart = this._weekStart(latestDate);
      const start       = this._addDays(anchorStart, this._offset * 7);
      const days        = Array.from({ length: 7 }, (_, i) => this._addDays(start, i));
 
      const locale = getLocale();
      const labels  = days.map(d => new Date(d + "T00:00:00")
        .toLocaleDateString(locale, { weekday: "short", day: "numeric" }));
      const drank   = days.map(d => this._raw[d] ? this._raw[d].drank   : 0);
      const offered = days.map(d => this._raw[d] ? this._raw[d].offered : 0);
      const marked = days.map(d => this._raw[d] ? this._raw[d].marked : 0);

      const startFmt = new Date(start + "T00:00:00")
        .toLocaleDateString(locale, { day: "numeric", month: "short" });
      const endFmt   = new Date(this._addDays(start, 6) + "T00:00:00")
        .toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });
 
      return { labels, drank, offered, marked, periodLabel: `${startFmt} – ${endFmt}` };
    }
 
    // ── G. Month view ─────────────────────────────────────────────────────
    // Every day of the target month, one stacked bar per day
    _monthData() {
      const keys      = this._sortedKeys();
      const latestDt  = new Date(keys[keys.length - 1] + "T00:00:00");
      let year  = latestDt.getFullYear();
      let month = latestDt.getMonth() + this._offset;
      while (month > 11) { month -= 12; year++; }
      while (month < 0)  { month += 12; year--; }
 
      const days = new Date(year, month + 1, 0).getDate();
      const labels = [], drank = [], offered = [], marked = []; 
 
      for (let day = 1; day <= days; day++) {
        const d = `${year}-${String(month + 1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
        labels.push(String(day));
        drank.push(this._raw[d]   ? this._raw[d].drank   : 0);
        offered.push(this._raw[d] ? this._raw[d].offered : 0);
        marked.push(this._raw[d] ? this._raw[d].marked : 0);
      }
 
      const locale = getLocale();
      const periodLabel = new Date(year, month, 1)
        .toLocaleDateString(locale, { month: "long", year: "numeric" });
 
      return { labels, drank, offered, marked, periodLabel };
    }
 
    // ── H. Year view ──────────────────────────────────────────────────────
    // 12 monthly totals for the target year
    _yearData() {
      const keys       = this._sortedKeys();
      const latestYear = parseInt(keys[keys.length - 1].slice(0, 4));
      const year       = latestYear + this._offset;
      const MONTHS_EN  = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      const MONTHS_IT  = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
      const locale = getLocale();
      const MONTHS = locale.startsWith('it') ? MONTHS_IT : MONTHS_EN;
      const drank      = new Array(12).fill(0);
      const offered    = new Array(12).fill(0);
      const marked     = new Array(12).fill(0);
 
      keys.forEach(d => {
        if (parseInt(d.slice(0, 4)) === year) {
          const m = parseInt(d.slice(5, 7)) - 1;
          drank[m]   += this._raw[d].drank || 0;
          offered[m] += this._raw[d].offered || 0;
          marked[m]  += this._raw[d].marked || 0;
        }
      });
 
      return { labels: MONTHS, drank, offered, marked, periodLabel: String(year) };
    }
 
    // ── I. Route to the active view ───────────────────────────────────────
    _viewData() {
      if (this._view === "week")  return this._weekData();
      if (this._view === "month") return this._monthData();
      return this._yearData();
    }
 
    // ── J. Compute summary metrics ────────────────────────────────────────
    _metrics(data) {
      const totalDrank   = data.drank.reduce((a, b) => a + b, 0);
      const totalOffered = data.offered.reduce((a, b) => a + b, 0);
      const totalMarked = data.marked.reduce((a, b) => a + b, 0);
      const activeDays   = data.drank.filter(v => v > 0).length;
      const avg          = activeDays ? ((totalDrank+totalMarked) / activeDays).toFixed(1) : "0.0";
      const ratio        = (totalDrank > 0 || totalMarked > 0) ? Math.round((totalOffered / (totalDrank+totalMarked)) * 100) : 0;
      const generosity   = ratio >= 75 ? t("high") : ratio >= 40 ? t("mid") : t("low");
      return { totalDrank, totalOffered, totalMarked, avg, ratio, generosity };
    }
 
    // ── K. Render metric cards ────────────────────────────────────────────
    _renderMetrics(m) {
      const el = this._shadow.getElementById("metrics");
      if (!el) return;
      // Replace {avg} placeholder in translation
      const avgLabel = t("avg-per-active-day").replace("{avg}", m.avg);
      el.innerHTML = `
        <div class="metric">
          <div class="metric-label">${t("total-drank")}</div>
          <div class="metric-val amber">${m.totalDrank+m.totalMarked}</div>
          <div class="metric-sub">${avgLabel}</div>
        </div>
        <div class="metric">
          <div class="metric-label">${t("total-offered")}</div>
          <div class="metric-val teal">${m.totalOffered}</div>
          <div class="metric-sub">${m.ratio}%</div>
        </div>
        <div class="metric">
          <div class="metric-label">${t("generosity")}</div>
          <div class="metric-val">${m.generosity}</div>
          <!--
          <div class="metric-sub">${m.ratio}% ${t("share-rate")}</div>
          -->
        </div>`;
    }
 
    // ── L. Update nav label + disable arrows at data boundaries ──────────
    _renderNav(data) {
      const label = this._shadow.getElementById("period-label");
      const prev  = this._shadow.getElementById("btn-prev");
      const next  = this._shadow.getElementById("btn-next");
      if (label) label.textContent = data.periodLabel;
 
      const keys         = this._sortedKeys();
      const latestDate   = keys[keys.length - 1];
      const earliestDate = keys[0];
      let atLatest = false, atEarliest = false;
 
      if (this._view === "week") {
        const latestWS   = this._weekStart(latestDate);
        const earliestWS = this._weekStart(earliestDate);
        const currentWS  = this._addDays(latestWS, this._offset * 7);
        atLatest   = currentWS >= latestWS;
        atEarliest = currentWS <= earliestWS;
      } else if (this._view === "month") {
        atLatest   = this._offset >= 0;
        atEarliest = this._offset <= -24;
      } else {
        atLatest   = this._offset >= 0;
        atEarliest = this._offset <= -10;
      }
 
      if (prev) prev.disabled = atEarliest;
      if (next) next.disabled = atLatest;
    }
 
    // ── M. Draw / redraw the stacked bar chart ────────────────────────────
    _renderChart(data) {
      const canvas = this._shadow.getElementById("chart");
      if (!canvas || typeof Chart === "undefined") return;
      if (this._chart) this._chart.destroy();

      const isMonth = this._view === "month";
      const isWeek = this._view === "week";

      // Register weekend indicator plugin for week view
      let plugins = [];
      if (isWeek) {
        const weekendPlugin = {
          id: 'weekendIndicator',
          afterDraw: (chart) => {
            const ctx = chart.ctx;
            const xAxis = chart.scales.x;
            const labels = chart.data.labels;
            
            // In week view, indices 5 and 6 are Saturday and Sunday
            const weekendIndices = [5, 6];
            
            weekendIndices.forEach(index => {
              if (index < labels.length) {
                const x = xAxis.getPixelForValue(index);
                const tick = xAxis.ticks[index];
                if (!tick) return;
                
                const labelSize = ctx.measureText(tick.label);
                const textWidth = labelSize.width;
                const fontSize = xAxis.options.ticks.font.size;
                const padding = 6;
                const pillHeight = fontSize + 4;
                const pillWidth = textWidth + padding * 2;
                const pillX = x - pillWidth / 2;
                const pillY = xAxis.top - pillHeight - 4 + 30;
                const pillRadius = pillHeight / 2;
                
                // Draw pill background
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(pillX + pillRadius, pillY);
                ctx.lineTo(pillX + pillWidth - pillRadius, pillY);
                ctx.arcTo(pillX + pillWidth, pillY, pillX + pillWidth, pillY + pillRadius, pillRadius);
                ctx.lineTo(pillX + pillWidth, pillY + pillHeight - pillRadius);
                ctx.arcTo(pillX + pillWidth, pillY + pillHeight, pillX + pillWidth - pillRadius, pillY + pillHeight, pillRadius);
                ctx.lineTo(pillX + pillRadius, pillY + pillHeight);
                ctx.arcTo(pillX, pillY + pillHeight, pillX, pillY + pillHeight - pillRadius, pillRadius);
                ctx.lineTo(pillX, pillY + pillRadius);
                ctx.arcTo(pillX, pillY, pillX + pillRadius, pillY, pillRadius);
                ctx.closePath();
                ctx.fillStyle = 'rgba(93, 64, 55, 0.12)';
                ctx.fill();
                ctx.restore();
              }
            });
          }
        };

        plugins = [ weekendPlugin ];
      } 

      const drankLabel = t("drank");
      const offeredLabel = t("offered");
      const markedLabel = t("marked");
      this._chart = new Chart(canvas.getContext("2d"), {
        type: "bar",
        data: {
          labels: data.labels,
          datasets: [
            {
              label: drankLabel,
              data: data.drank,
              backgroundColor: "#5D4037",
              borderSkipped: false,
              stack: "coffee",
            },
            {
              label: offeredLabel,
              data: data.offered,
              backgroundColor: "#A28077",
              borderSkipped: false,
              stack: "coffee",
            },
            {
              label: markedLabel,
              data: data.marked,
              backgroundColor: "#eae5e4",
              borderSkipped: false,
              stack: "coffee",
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              mode: "index",
              callbacks: {
                label: ctx => ` ${ctx.dataset.label}: ${ctx.parsed.y}`,
                footer: items => `Total: ${items.reduce((s, i) => s + i.parsed.y, 0)}`
              }
            }
          },
          scales: {
            x: {
              stacked: true,
              grid: { display: false },
              border: { display: false },
              ticks: {
                color: "#888780",
                font: { size: isMonth ? 9 : 11 },
                autoSkip: isMonth,
                maxTicksLimit: isMonth ? 15 : undefined,
                maxRotation: 0,
              }
            },
            y: {
              stacked: true,
              beginAtZero: true,
              grid: { color: "rgba(128,128,128,0.1)" },
              border: { display: false },
              ticks: {
                color: "#888780",
                font: { size: 11 },
                stepSize: 1,
                precision: 0,
              }
            }
          },
          animation: { duration: 250 },
        },
        plugins: plugins,
      });
    }

    _renderTitle(titleText) {
      const el = this._shadow.getElementById("title");
      if (!el) return;
      el.innerHTML = `${titleText}`;
    }
 
    // ── N. Master update ──────────────────────────────────────────────────
    _update() {
      if (!this._shadow.getElementById("metrics")) return;
      const data = this._viewData();
      this._renderMetrics(this._metrics(data));
      this._renderNav(data);
      this._renderChart(data);
    }
 
    // ── O. Switch view + reset offset ────────────────────────────────────
    _switchView(view) {
      this._view   = view;
      this._offset = 0;
      this._shadow.querySelectorAll(".tab").forEach(tab => {
        tab.classList.toggle("active", tab.dataset.view === view);
      });
      this._update();
    }
 
    // ── P. Step forward / backward one period ────────────────────────────
    _navigate(dir) {
      this._offset += dir;
      this._update();
    }
 
    // ── Q. Bind all internal events ───────────────────────────────────────
    _bindEvents() {
      this._shadow.querySelectorAll(".tab").forEach(tab =>
        tab.addEventListener("click", () => this._switchView(tab.dataset.view))
      );
      this._shadow.getElementById("btn-prev")
        .addEventListener("click", () => this._navigate(-1));
      this._shadow.getElementById("btn-next")
        .addEventListener("click", () => this._navigate(1));
    }
 
    // ── R. SVG chevrons ───────────────────────────────────────────────────
    _chevronLeft()  { return `<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`; }
    _chevronRight() { return `<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`; }
 
    // ── S. Full initial render ────────────────────────────────────────────
    _render() {
      this._shadow.innerHTML = `
        <style>
          *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
          :host { display: block; font-family: system-ui, sans-serif; }
 
          .card {
            background: #fff;
            border: 0.5px solid #e0dfd7;
            border-radius: 12px;
            padding: 1.25rem;
            /* max-width: 700px; */
          }
 
          .header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 1rem;
            gap: 12px;
            flex-wrap: wrap;
          }
 
          .title {
            font-size: 15px;
            font-weight: 500;
            color: #1a1a1a;
            display: flex;
            align-items: center;
            gap: 8px;
          }
 
          .title-icon {
            width: 28px; height: 28px;
            background: #5D4037;
            border-radius: 8px;
            display: flex; align-items: center; justify-content: center;
            flex-shrink: 0;
          }
 
          .tabs {
            display: flex;
            gap: 2px;
            background: #f5f5f3;
            border-radius: 8px;
            padding: 3px;
          }
 
          .tab {
            font: inherit;
            font-size: 12px;
            font-weight: 500;
            padding: 5px 12px;
            border-radius: 6px;
            border: none;
            background: transparent;
            color: #888;
            cursor: pointer;
            transition: background 0.12s, color 0.12s;
          }
 
          .tab.active {
            background: #fff;
            color: #1a1a1a;
            border: 0.5px solid #e0dfd7;
          }
 
          .metrics {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 8px;
            margin-bottom: 1rem;
          }
 
          .metric        { background: #f5f5f3; border-radius: 8px; padding: 10px 12px; }
          .metric-label  { font-size: 11px; color: #aaa; margin-bottom: 4px; }
          .metric-val    { font-size: 22px; font-weight: 500; color: #1a1a1a; line-height: 1; }
          .metric-val.amber { color: #5D4037; }
          .metric-val.teal  { color: #0F6E56; }
          .metric-sub    { font-size: 11px; color: #aaa; margin-top: 3px; }
 
          .nav {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 10px;
            gap: 8px;
          }
 
          .nav-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px; height: 28px;
            border-radius: 8px;
            border: 0.5px solid #e0dfd7;
            background: #fff;
            color: #888;
            cursor: pointer;
            transition: background 0.12s, color 0.12s;
            flex-shrink: 0;
          }
 
          .nav-btn:hover:not(:disabled) { background: #f5f5f3; color: #1a1a1a; }
          .nav-btn:disabled { opacity: 0.3; cursor: default; }
 
          .nav-label {
            font-size: 13px;
            font-weight: 500;
            color: #1a1a1a;
            text-align: center;
            flex: 1;
          }
 
          .legend {
            display: flex;
            gap: 16px;
            margin-bottom: 10px;
            flex-wrap: wrap;
          }
 
          .legend-item {
            display: flex;
            align-items: center;
            gap: 5px;
            font-size: 12px;
            color: #888;
          }
 
          .legend-sq {
            width: 10px; height: 10px;
            border-radius: 2px;
            flex-shrink: 0;
          }
 
          .chart-wrap {
            position: relative;
            width: 100%;
            height: 220px;
          }
 
          @media (max-width: 400px) {
            /*.metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }*/
            .metric-val { font-size: 18px; }
            .card { padding: 1rem; }
          }
 
          /*
          @media (prefers-color-scheme: dark) {
            .card       { background: #1e1e1c; border-color: #333330; }
            .title      { color: #f0ede8; }
            .tab        { color: #888; }
            .tab.active { background: #2a2a28; color: #f0ede8; border-color: #333330; }
            .tabs       { background: #2a2a28; }
            .metric     { background: #2a2a28; }
            .metric-val { color: #f0ede8; }
            .nav-btn    { background: #1e1e1c; border-color: #333330; color: #888; }
            .nav-btn:hover:not(:disabled) { background: #2a2a28; color: #f0ede8; }
            .nav-label  { color: #f0ede8; }
          }
          */
        </style>
 
        <div class="card">
          <div class="header">
            <div class="title">
              <div class="title-icon">
                <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                  <path d="M3 6h10v7a3 3 0 01-3 3H6a3 3 0 01-3-3V6z" stroke="#F5EDEB" stroke-width="1.3" stroke-linejoin="round"/>
                  <path d="M13 8h1a2 2 0 010 4h-1" stroke="#F5EDEB" stroke-width="1.3" stroke-linecap="round"/>
                  <path d="M6 3c0-1 1-1.5 1-2.5M9 3c0-1 1-1.5 1-2.5" stroke="#F5EDEB" stroke-width="1.2" stroke-linecap="round"/>
                </svg>
              </div>
              <span id="title">${this._title}</span>
            </div>
            <div class="tabs">
              <button class="tab active" data-view="week">${t("week")}</button>
              <button class="tab" data-view="month">${t("month")}</button>
              <button class="tab" data-view="year">${t("year")}</button>
            </div>
          </div>
 
          <div class="metrics" id="metrics"></div>
 
          <div class="nav">
            <button class="nav-btn" id="btn-prev" aria-label="${t("previous-period")}">${this._chevronLeft()}</button>
            <span class="nav-label" id="period-label"></span>
            <button class="nav-btn" id="btn-next" aria-label="${t("next-period")}">${this._chevronRight()}</button>
          </div>
 
          <div class="legend">
            <span class="legend-item">
              <span class="legend-sq" style="background:#5D4037"></span>${t("drank")}
            </span>
            <span class="legend-item">
              <span class="legend-sq" style="background:#A28077;outline:1px dashed #0F6E56;outline-offset:1px"></span>${t("offered")}
            </span>
            <span class="legend-item">
              <span class="legend-sq" style="background:#eae5e4;"></span>${t("marked")}
            </span>
          </div>
 
          <div class="chart-wrap">
            <canvas id="chart" role="img"
              aria-label="Stacked bar chart comparing drank vs offered coffee counts">
              Coffee stats: drank vs offered per period.
            </canvas>
          </div>
        </div>
      `;
    }
  }

  export { CoffeeStats }